// Estimator notes price stripping (D6, Peter 2026-10-06: "estimator notes
// feed the plan, with their prices removed").
//
// An estimator's own scope notes carry the operations, the
// order they happen in and the business's own words for them, which is what
// the Stage 3 plan should learn from. They also carry the estimator's sums:
// rates, line totals, admin and margin lines, and crew-day labour estimates
// ("2men 2days"). No prompt may see those, because a price in a prompt becomes
// a price source.
//
// Round 6 design, by construction, on LINES (orchestrator scope ruling 2,
// 2026-10-07). Five rounds of cleaning prices out of a line never converged:
// every rule that kept part of a priced line let some price through.
//
//   0. Each line is first put in Unicode NFKC form, so full-width and
//      superscript characters ("＄１，２００", "³⁹¹⁰") become the plain "$1,200"
//      and "3910" the rules below read. A unit power written straight after
//      an m ("m²", "m³") is kept as typed. A line that then holds a currency
//      symbol the signal list does not name ("₹"), or any number character
//      outside 0-9 (Arabic-Indic, Devanagari, Ethiopic, Tamil numerals and
//      the like), is dropped whole (review 7 and the final review).
//   1. A line that carries ANY signal from PRICE_LINE_SIGNALS is dropped
//      whole. Nothing of it is cleaned or kept.
//   2. On every other line, a number survives only when a unit from
//      ESTIMATOR_NOTES_UNITS follows it (at most one space between), or as
//      part of a size: every side a length with a unit ("16m x 1.2m"), or
//      every side a plain number and the last carrying mm, cm, m or metres
//      ("1200 x 600 mm", "90x45mm", "85 x 30m"). Every other number is
//      removed, with an operator written straight before it (x, *, /, +, -,
//      ~). A side multiplied onto a quantity that is not a size goes with its
//      unit ("46sqm x 35 m2" keeps "46sqm"). Spaced thousands ("1 200") are
//      one figure, so they are removed whole and never leave a false "200".
//   3. A kept line that the removals leave with no letter or digit, or that
//      they turn into a signal ("call 5 out"), is dropped.
//   4. The kept lines, in their order, joined with newlines and trimmed.
//
// Idempotent by construction: after a pass no kept line carries a signal, and
// every number left is a kept quantity or a size whose text, and the text to
// its right, the pass did not change. A second pass therefore drops nothing
// and removes nothing.

import { isEstimatorNotesDocument, resolveDocumentClassification } from './document-classification';
import { detectEstimatorPricedNotes } from './estimator-notes-detector';

/**
 * Every word or symbol that marks a line of estimator notes as a price, a
 * rate, a total or a crew-time labour estimate. Case-insensitive. A word
 * matches only as a whole word (no letter beside it; a digit may touch it, so
 * "2men", "1895ex" and "16hrs" match). A space inside a signal matches any run
 * of spaces. "<digit>k" is a digit glued to k ("2k", "2.5k", "1k2").
 *
 * A line holding any of these is dropped whole.
 */
export const PRICE_LINE_SIGNALS: readonly string[] = Object.freeze([
    // Money signs and currency codes, percent, rate and sum symbols.
    '$', 'A$', 'AUD', '¢', '£', '€', '¥', 'USD', 'NZD', 'EUR', 'GBP', '%', '@', '=', '+GST',
    // Pricing words.
    'price', 'prices', 'pricing', 'priced', 'cost', 'costs', 'costing', 'costed', 'charge', 'charges', 'charged',
    'rate', 'rates', 'total', 'totals', 'subtotal', 'sub-total', 'sum', 'lump', 'margin', 'markup', 'mark-up',
    'profit', 'admin', 'discount', 'gst', 'inc', 'incl', 'ex', 'excl', 'dollar', 'dollars', 'bucks', 'grand',
    'fee', 'fees', 'budget', 'quote', 'quoted', 'quoting', 'percent', 'percentage', 'cent', 'cents',
    // Rates per unit or time.
    'per', 'each', 'ea', 'p/h', 'ph', '/hr', '/h', '/day', '/wk', '/week', 'hourly', 'daily', 'weekly',
    'callout', 'call-out', 'call out',
    // Prices written in words.
    'hundred', 'thousand', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety', '<digit>k',
    // Crew and time: the estimator's route to a labour price.
    'men', 'man', 'guys', 'blokes', 'workers', 'worker', 'labour', 'labor', 'labourer', 'labourers', 'tradie',
    'tradies', 'crew', 'hands', 'day', 'days', 'hr', 'hrs', 'hour', 'hours', 'week', 'weeks', 'manday', 'mandays',
    'man-day', 'man-days',
    // More people and time words (review 6): "two people three nights",
    // "three staff for a fortnight". Trade names (carpenter, tiler) are not
    // signals, because "carpenter to refit the door" is an operation, so
    // "two carpenters" on its own is kept.
    'people', 'person', 'persons', 'staff', 'apprentice', 'apprentices', 'offsider', 'offsiders', 'helper',
    'helpers', 'night', 'nights', 'morning', 'mornings', 'afternoon', 'afternoons', 'arvo', 'arvos', 'fortnight',
    'fortnights', 'weekend', 'weekends', 'shifts', 'month', 'months', 'tradesman', 'tradesmen',
]);

/**
 * The closed list of measurement units. A number followed by one of these
 * (at most one space between) is a measured quantity and is kept. Nothing
 * else keeps a number. The single letters "m" and "t" count only in lower
 * case, because "M 480 L 1200" is materials and labour. "L" on its own is
 * not a unit for the same reason: write "litres".
 */
export const ESTIMATOR_NOTES_UNITS: readonly string[] = Object.freeze([
    'square metres', 'square meters', 'linear metres', 'linear meters', 'lineal metres', 'lineal meters',
    'metres', 'meters', 'metre', 'meter',
    'litres', 'liters', 'litre', 'liter',
    'tonnes', 'tonne',
    'sq m', 'sqm', 'l/m', 'lm', 'm²', 'm³', 'm2', 'm3', 'mm', 'cm', 'kg',
    'm', 't',
]);

// The units a size is built from.
const LENGTH_UNITS: readonly string[] = ['metres', 'meters', 'metre', 'meter', 'mm', 'cm', 'm'];

function escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
}

// ─── Line rule ─────────────────────────────────────────────────────────────

function signalPattern(signal: string): string {
    if (signal === '<digit>k') return '(?<=\\d)[kK](?![A-Za-z])';
    const body = [...signal].map(char => (char === ' ' ? '[ \\t]+' : escapeRegex(char))).join('');
    const left = /^[A-Za-z]/.test(signal) ? '(?<![A-Za-z])' : '';
    const right = /[A-Za-z]$/.test(signal) ? '(?![A-Za-z])' : '';
    return `${left}${body}${right}`;
}

const signalRegexCache = new WeakMap<readonly string[], RegExp>();

function signalRegex(signals: readonly string[]): RegExp {
    let re = signalRegexCache.get(signals);
    if (!re) {
        re = signals.length
            ? new RegExp([...signals].sort((a, b) => b.length - a.length).map(signalPattern).join('|'), 'i')
            : /(?!)/;
        signalRegexCache.set(signals, re);
    }
    return re;
}

/**
 * The first price signal on a line, or null when it carries none. The signal
 * list is a parameter only so a test can show that each signal is what drops
 * its line; every production caller uses PRICE_LINE_SIGNALS.
 */
export function priceLineSignal(line: unknown, signals: readonly string[] = PRICE_LINE_SIGNALS): string | null {
    if (typeof line !== 'string' || !line) return null;
    return line.match(signalRegex(signals))?.[0] ?? null;
}

// ─── Number rule on kept lines ─────────────────────────────────────────────

// A unit as a pattern: single letters exactly as listed, everything else in
// any case, with a space inside the unit written as one space or tab.
function unitPattern(units: readonly string[]): string {
    const alternatives = [...units]
        .sort((a, b) => b.length - a.length)
        .map(unit => {
            if (unit.length === 1) return escapeRegex(unit);
            return [...unit].map(char => {
                if (char === ' ') return '[ \\t]';
                const lower = char.toLowerCase();
                const upper = char.toUpperCase();
                return lower !== upper ? `[${lower}${upper}]` : escapeRegex(char);
            }).join('');
        });
    return `(?:${alternatives.join('|')})`;
}

const UNIT = unitPattern(ESTIMATOR_NOTES_UNITS);
const LENGTH_UNIT = unitPattern(LENGTH_UNITS);
// Nothing word-like may follow a unit: "2 men" is not "2 m", "5mm3" is not "5mm".
const BOUNDARY = '(?![A-Za-z0-9²³])';
const NUM = String.raw`(?:\d+(?:[.,]\d+)*|\.\d+)`;
const GAP = '[ \\t]?';
const TIMES = `${GAP}[x×*]${GAP}`;

// The next figure: spaced thousands ("1 200", "12 345 678", not glued to a
// letter like "r10 300mm") as one figure, otherwise digits with "." or ","
// groups, or a bare decimal (".75").
const FIGURE_RE = /(?<![\dA-Za-z.,])\d{1,3}(?:[ \t]\d{3})+(?![\d.,]*\d)|\d+(?:[.,]\d+)*|(?<!\d)\.\d+/;
const SPACED_FIGURE_RE = /^\d{1,3}[ \t]\d{3}/;
// The "2" or "3" of a unit written on its own ("m2 tiles", "/m3") is part of
// the unit, not a number.
const UNIT_POWER_RE = /^[23](?![A-Za-z0-9²³])/;
const UNIT_POWER_BEFORE_RE = /(?<![A-Za-z])[mM]$/;
const UNIT_AFTER_RE = new RegExp(`^${GAP}${UNIT}${BOUNDARY}`);
// A size: every side a length with a unit, or every side a plain number and
// the last side carrying a length unit.
const SIZE_RE = new RegExp(
    `^(?:(?:${NUM}${GAP}${LENGTH_UNIT}${TIMES})+${NUM}${GAP}${LENGTH_UNIT}|(?:${NUM}${TIMES})+${NUM}${GAP}${LENGTH_UNIT})${BOUNDARY}`,
);
// A standalone x written before a number, since the last decision.
const TIMES_BEFORE_RE = /(?<![A-Za-z])[x×*][ \t]*$/;
// An operator written straight before a removed number, read right to left.
const OPERATOR_BEFORE_RE = /(?:(?<![A-Za-z])[x×]|[*/~+-])[ \t]*$/;

interface Span { start: number; end: number }

function nextFigure(line: string, from: number): Span | null {
    let at = from;
    while (at < line.length) {
        const match = line.slice(at).match(FIGURE_RE);
        if (!match || match.index === undefined) return null;
        const start = at + match.index;
        const end = start + match[0].length;
        if (UNIT_POWER_RE.test(line.slice(start)) && UNIT_POWER_BEFORE_RE.test(line.slice(0, start))) {
            at = start + 1;
            continue;
        }
        return { start, end };
    }
    return null;
}

function extendBefore(line: string, start: number, floor: number): number {
    let at = start;
    for (let match = line.slice(floor, at).match(OPERATOR_BEFORE_RE); match && match[0].length > 0;
        match = line.slice(floor, at).match(OPERATOR_BEFORE_RE)) {
        at -= match[0].length;
    }
    return at;
}

function removedSpans(line: string): Span[] {
    const spans: Span[] = [];
    // Everything before `floor` has been decided: a kept quantity or a removal.
    let floor = 0;
    for (let figure = nextFigure(line, floor); figure; figure = nextFigure(line, floor)) {
        const { start, end } = figure;
        const spaced = SPACED_FIGURE_RE.test(line.slice(start, end));
        const after = line.slice(end);

        // A side multiplied onto something that is not a size: the figure
        // goes with its unit and the x.
        if (TIMES_BEFORE_RE.test(line.slice(floor, start))) {
            const unit = spaced ? null : after.match(UNIT_AFTER_RE);
            const spanEnd = end + (unit ? unit[0].length : 0);
            spans.push({ start: extendBefore(line, start, floor), end: spanEnd });
            floor = spanEnd;
            continue;
        }
        if (!spaced) {
            const size = line.slice(start).match(SIZE_RE);
            if (size) {
                floor = start + size[0].length;
                continue;
            }
            const unit = after.match(UNIT_AFTER_RE);
            if (unit) {
                floor = end + unit[0].length;
                continue;
            }
        }
        spans.push({ start: extendBefore(line, start, floor), end });
        floor = end;
    }
    return spans;
}

// Where a removal would join two letters or digits, or a dot or comma to a
// digit, a space is left. So a kept unit can never become the start of a
// longer word, and a kept number can never grow a decimal part.
function joinNeedsSpace(before: string, next: string, afterNext: string): boolean {
    if (/[A-Za-z0-9²³]$/.test(before) && /[A-Za-z0-9²³]/.test(next)) return true;
    if (/[.,]$/.test(before) && /\d/.test(next)) return true;
    return /\d$/.test(before) && /[.,]/.test(next) && /\d/.test(afterNext);
}

function removeSpans(line: string, spans: Span[]): string {
    let out = '';
    let at = 0;
    for (const span of spans) {
        out += line.slice(at, span.start);
        if (joinNeedsSpace(out, line.charAt(span.end), line.charAt(span.end + 1))) out += ' ';
        at = span.end;
    }
    return out + line.slice(at);
}

// The line in Unicode NFKC form, with an m² or m³ unit power kept as typed
// (NFKC would turn it into m2 or m3). Private-use stand-ins carry the two
// powers through the normalisation; NFKC leaves private-use characters alone.
const UNIT_POWER_STAND_IN: Readonly<Record<string, string>> = { '\u00B2': '\uE000', '\u00B3': '\uE001' };
// NFKC with the two unit powers held as stand-ins. holdsUnreadableMoney reads
// this form, so a kept m² or m³ is never taken for a stray number character.
function guardedPlainForm(line: string): string {
    return line
        .replace(/(?<=[mM])[\u00B2\u00B3]/g, power => UNIT_POWER_STAND_IN[power] || power)
        .normalize('NFKC');
}

function restoreUnitPowers(line: string): string {
    return line.replace(/\uE000/g, '\u00B2').replace(/\uE001/g, '\u00B3');
}

// The currency symbols the signal list already names. The listed signals own
// them, so the mutation test can show each one is what drops its line.
const LISTED_CURRENCY_SYMBOLS: ReadonlySet<string> = new Set(
    PRICE_LINE_SIGNALS.filter(signal => /^\p{Sc}$/u.test(signal)),
);

// A currency symbol the list does not name, or any number character that is
// not 0-9 (a digit, numeral or number sign NFKC left as it is): the number
// rule below cannot read the line, so it is dropped.
function holdsUnreadableMoney(line: string): boolean {
    for (const char of line) {
        if (/\p{Sc}/u.test(char) && !LISTED_CURRENCY_SYMBOLS.has(char)) return true;
        if (/\p{N}/u.test(char) && !/[0-9]/.test(char)) return true;
    }
    return false;
}

// The line as a prompt may read it, or null when it is dropped.
function stripLine(rawLine: string, signals: readonly string[]): string | null {
    // A private-use character (from an unmapped PDF font, or typed) could
    // collide with the m² / m³ stand-ins, so such a line is dropped whole.
    if (/\p{Co}/u.test(rawLine)) return null;
    const guarded = guardedPlainForm(rawLine);
    if (holdsUnreadableMoney(guarded)) return null;
    const line = restoreUnitPowers(guarded);
    if (priceLineSignal(line, signals)) return null;
    const spans = removedSpans(line);
    if (spans.length === 0) return line;
    const indent = line.match(/^[ \t]*/)?.[0] || '';
    const body = removeSpans(line, spans).trim().replace(/[ \t]{2,}/g, ' ');
    // A line left with no letters or digits carried only a figure.
    if (!/[A-Za-z0-9]/.test(body)) return null;
    // A removal that closes up a signal ("call 5 out") drops the line too.
    if (priceLineSignal(body, signals)) return null;
    return indent + body;
}

// The request keeps its prices: an RFQ or a scope of works is the client's
// own text, read as written.
const REQUEST_CLASSES: ReadonlySet<string> = new Set(['rfq', 'scope_of_works']);

function documentOwnText(document: any): string {
    for (const key of ['content', 'text', 'textContent', 'extractedText']) {
        const value = document?.[key];
        if (typeof value === 'string' && value.trim()) return value;
    }
    return '';
}

/**
 * True when a prompt must read a document as the business's own priced notes
 * (CN1): it is stored as estimator notes (by the content classifier or an
 * operator), or its text reads as priced working notes and it is not the
 * client's request. Never decided by the file name. `text` is the text the
 * caller is about to send; without it the document's own stored text is read.
 */
export function isEstimatorNotesForPrompt(document: unknown, text?: unknown): boolean {
    if (isEstimatorNotesDocument(document)) return true;
    if (!document || typeof document !== 'object') return false;
    if (REQUEST_CLASSES.has(String(resolveDocumentClassification(document).classification))) return false;
    const value = typeof text === 'string' ? text : documentOwnText(document);
    return detectEstimatorPricedNotes(value).isEstimatorNotes;
}

/**
 * The role a document header carries when the document is the business's own
 * priced notes, so no prompt reads them as the client's request. Empty for
 * every other document. Both document paths (condenseDocuments and the
 * adaptive slow path) use it, so the label cannot drift between them, and it
 * uses the same test as estimatorNotesPromptText, so a header says "prices
 * removed" exactly when they were.
 */
export function estimatorNotesHeaderRole(document: unknown): string {
    return isEstimatorNotesForPrompt(document)
        ? " [estimator notes: the business's own scope notes, prices removed, not the client's request]"
        : '';
}

/**
 * The text a prompt may read from a document: the business's own priced
 * notes with their price lines dropped, every other document unchanged.
 * Every prompt that reads document text goes through this, so priced notes
 * reach no model with their prices, whatever the file is called and whether
 * or not they were stored before import stripping (CN1).
 */
export function estimatorNotesPromptText(document: unknown, text: unknown): string {
    const value = typeof text === 'string' ? text : '';
    return isEstimatorNotesForPrompt(document, value) ? stripEstimatorNotePrices(value) : value;
}

/**
 * Drop every line of estimator notes text that carries a price signal, and
 * on the other lines remove every number that is not a measured quantity or a
 * size. Pure and idempotent by construction (see the header). The signal list
 * is a parameter only for the mutation test; production uses the default.
 */
export function stripEstimatorNotePrices(text: unknown, signals: readonly string[] = PRICE_LINE_SIGNALS): string {
    if (typeof text !== 'string' || !text) return '';
    return text
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map(line => stripLine(line, signals))
        .filter((line): line is string => line !== null)
        .join('\n')
        .trim();
}
